import * as React from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Sober DS (v2) — Accordion. Dep-free disclosure list (no @radix-ui/react-accordion).
 * Each item owns local open state; the trigger is a native <button> with
 * aria-expanded, and the content collapses via a max-height/opacity CSS
 * transition. Items are separated by a single 1px outline (no colored borders).
 */
export type AccordionProps = React.HTMLAttributes<HTMLDivElement>;

export const Accordion = React.forwardRef<HTMLDivElement, AccordionProps>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('w-full', className)} {...props} />
  )
);
Accordion.displayName = 'Accordion';

export interface AccordionItemProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  title: React.ReactNode;
  defaultOpen?: boolean;
}

export const AccordionItem = React.forwardRef<HTMLDivElement, AccordionItemProps>(
  ({ className, title, defaultOpen = false, children, ...props }, ref) => {
    const [open, setOpen] = React.useState(defaultOpen);
    const contentId = React.useId();

    return (
      <div
        ref={ref}
        className={cn('border-b border-sober-outline last:border-0', className)}
        {...props}
      >
        <button
          type="button"
          aria-expanded={open}
          aria-controls={contentId}
          onClick={() => setOpen((v) => !v)}
          className={cn(
            'flex w-full items-center justify-between p-4 text-left text-so-14 font-bold text-sober-text',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sober-primary-fg'
          )}
        >
          <span className="min-w-0">{title}</span>
          <ChevronDown
            aria-hidden="true"
            className={cn(
              'size-4 shrink-0 text-sober-secondary transition-transform duration-200',
              open && 'rotate-180'
            )}
          />
        </button>
        <div
          id={contentId}
          role="region"
          className={cn(
            'overflow-hidden text-so-14 text-sober-secondary transition-all duration-200',
            open ? 'max-h-[400px] pb-4 opacity-100' : 'max-h-0 opacity-0'
          )}
        >
          {children}
        </div>
      </div>
    );
  }
);
AccordionItem.displayName = 'AccordionItem';
